import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, Card, Table, Button, Space, 
  Modal, message, Spin, Badge, Tooltip, Empty,
  Row, Col, Statistic, Avatar, Tag
} from 'antd';
import { 
  CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
  SafetyCertificateOutlined, ReloadOutlined, ArrowLeftOutlined,
  UserOutlined, GlobalOutlined, CalendarOutlined
} from '@ant-design/icons';

import { useAdmin } from '../../hooks/useAdmin';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const { Title, Text } = Typography;

const KYCManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUsers, verifyKYC, loading } = useAdmin();
  const [pendingKYC, setPendingKYC] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadKYCRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getUsers({ kyc_status: 'pending' });
      if (result.success) {
        setPendingKYC(result.data.users || []);
      }
    } catch (err) {
      message.error(t('manager.kyc.messages.load_error'));
    } finally {
      setIsLoading(false);
    }
  }, [getUsers, t]);

  useEffect(() => {
    loadKYCRequests();
  }, [loadKYCRequests]);

  const handleVerify = async (userId, status, notes = '') => {
    try {
      const res = await verifyKYC(userId, { status, notes });
      if (res.success) {
        message.success(status === 'verified' ? t('manager.kyc.messages.approve_success') : t('manager.kyc.messages.reject_success'));
        loadKYCRequests();
      } else {
        message.error(res.message || t('common.error'));
      }
    } catch (err) {
      message.error(t('common.error'));
    }
  };

  const columns = [
    {
      title: t('manager.kyc.table.user'),
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar className="bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
            <UserOutlined />
          </Avatar>
          <Space direction="vertical" size={0}>
            <Text className="text-white font-bold text-xs">{record.username}</Text>
            <Text className="text-slate-500 text-[10px]">{record.email}</Text>
          </Space>
        </Space>
      )
    },
    {
      title: t('manager.kyc.table.submitted'),
      dataIndex: 'updated_at',
      key: 'date',
      render: (date) => (
        <Space className="text-slate-400 text-xs">
          <CalendarOutlined className="text-slate-600" />
          {moment(date).format('DD/MM/YYYY HH:mm')}
        </Space>
      )
    },
    {
      title: t('manager.kyc.table.country'),
      dataIndex: 'country',
      key: 'country',
      render: (c) => (
        <Space className="text-slate-400 text-xs">
          <GlobalOutlined className="text-slate-600" />
          {c || 'N/A'}
        </Space>
      )
    },
    {
      title: t('manager.kyc.table.status'),
      key: 'status',
      render: () => <Tag color="orange" className="m-0 text-[10px] font-black uppercase tracking-widest bg-orange-500/10 text-orange-400 border-orange-500/20">{t('admin.users_management.table.kyc.pending')}</Tag>
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title={t('manager.kyc.actions.view')}>
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => navigate(`/admin/users/${record.id}`)}
              className="bg-white/5 border-white/10 text-indigo-400 hover:text-indigo-300 hover:border-indigo-400/50"
            />
          </Tooltip>
          <Button 
            icon={<CheckCircleOutlined />} 
            onClick={() => handleVerify(record.id, 'verified')}
            className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20 font-bold text-xs"
          >
            {t('manager.kyc.actions.approve')}
          </Button>
          <Button 
            danger 
            icon={<CloseCircleOutlined />} 
            onClick={() => {
              Modal.confirm({
                title: t('manager.withdrawals.modal.reject_title'),
                content: t('admin.user_modals.warning_desc'),
                okText: t('manager.kyc.actions.reject'),
                okType: 'danger',
                className: 'dark-modal',
                onOk: () => handleVerify(record.id, 'rejected')
              });
            }}
            className="bg-rose-500/10 text-rose-500 border-rose-500/20 hover:bg-rose-500/20 font-bold text-xs"
          >
            {t('manager.kyc.actions.reject')}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-950 text-slate-200 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <Space direction="vertical" size={0}>
          <Space align="center" className="mb-1">
            <Button 
              icon={<ArrowLeftOutlined />} 
              type="link" 
              onClick={() => navigate('/manager')}
              className="text-slate-500 hover:text-white p-0 mr-2"
            />
            <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
            <Title level={2} className="m-0 !text-white !font-black uppercase tracking-tight">
              {t('manager.kyc.title')}
            </Title>
          </Space>
          <Text className="text-slate-500 font-medium tracking-wide">
            {t('manager.kyc.subtitle')}
          </Text>
        </Space>
        
        <Space>
          <Card className="trading-card border-none py-1 px-4 bg-white/5">
            <Statistic 
              title={<Text className="text-slate-500 text-[10px] uppercase font-black">{t('manager.kyc.stats.total_pending')}</Text>}
              value={pendingKYC.length} 
              valueStyle={{ color: '#f59e0b', fontSize: '1.2rem', fontWeight: '900' }}
            />
          </Card>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={loadKYCRequests} 
            loading={isLoading}
            className="btn-pro bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            {t('manager.dashboard.refresh')}
          </Button>
        </Space>
      </div>

      <Card className="trading-card border-none overflow-hidden">
        <Table 
          columns={columns} 
          dataSource={pendingKYC} 
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          className="custom-table"
          locale={{ 
            emptyText: (
              <div className="py-12">
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<Text className="text-slate-500 font-bold uppercase tracking-widest text-xs">{t('manager.kyc.empty')}</Text>} 
                />
              </div>
            )
          }}
        />
      </Card>

      <style jsx="true">{`
        .custom-table .ant-table {
          background: transparent !important;
        }
        .custom-table .ant-table-thead > tr > th {
          background: transparent !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          color: #475569 !important;
          font-size: 10px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.1em !important;
          font-weight: 900 !important;
          padding: 16px !important;
        }
        .custom-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(255, 255, 255, 0.03) !important;
          padding: 16px !important;
        }
        .custom-table .ant-table-tbody > tr:hover > td {
          background: rgba(255, 255, 255, 0.02) !important;
        }
        .ant-pagination-item, .ant-pagination-prev, .ant-pagination-next {
          background: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        .ant-pagination-item-active {
          border-color: #6366f1 !important;
          background: #6366f1 !important;
        }
        .ant-pagination-item-active a {
          color: white !important;
        }
        .ant-pagination-item a {
          color: #94a3b8 !important;
        }
      `}</style>
    </div>
  );
};

export default KYCManagement;

